import React, { useState } from 'react';
import Papa from 'papaparse';

interface HeaderRequirement {
    id: string;
    label: string;
    description: string;
    example: string;
    required: boolean;
}

interface CSVImportModalProps {
    isOpen: boolean;
    onClose: () => void;
    /**
     * Called when the user confirms import.
     * - `data` is the parsed CSV rows.
     * - `zipFile` is the optional companion .zip of attachments (only present
     *   when `allowFileAttachments` is true AND the user picked one).
     */
    onImport: (data: any[], zipFile?: File) => Promise<void>;
    title: string;
    templateHeaders: string[];
    headerRequirements?: HeaderRequirement[];
    sampleData?: string;
    /**
     * When true, render a second file picker for an optional companion .zip
     * of attachments (e.g. PDFs referenced by the CSV's `pdf_filename`
     * column). The zip is passed to onImport — the parent decides how to
     * match entries to rows and upload them.
     */
    allowFileAttachments?: boolean;
}

export default function CSVImportModal({
    isOpen,
    onClose,
    onImport,
    title,
    templateHeaders,
    headerRequirements,
    sampleData,
    allowFileAttachments = false,
}: CSVImportModalProps) {
    const [file, setFile] = useState<File | null>(null);
    const [zipFile, setZipFile] = useState<File | null>(null);
    const [importing, setImporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [previewData, setPreviewData] = useState<any[]>([]);
    const [results, setResults] = useState<{ total: number; success: number; failed: number } | null>(null);
    const [step, setStep] = useState<'upload' | 'preview' | 'success'>('upload');

    if (!isOpen) return null;

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            setError(null);
            setResults(null);

            // Auto-parse for preview
            Papa.parse(selectedFile, {
                header: true,
                skipEmptyLines: true,
                transformHeader: (h) => {
                    const lowerH = h.trim().toLowerCase();
                    // Map to expected exact-case template header if it matches or contains it (like 'date (mm/dd/yyyy)' -> 'date')
                    const matched = (templateHeaders || []).find(th => 
                        lowerH === th.toLowerCase() || lowerH.startsWith(th.toLowerCase())
                    );
                    return matched || h.trim();
                },
                complete: (parseResults) => {
                    const data = parseResults.data;
                    const headers = parseResults.meta.fields || [];
                    
                    // Determine which headers are actually required
                    const requiredHeaders = headerRequirements 
                        ? headerRequirements.filter(r => r.required).map(r => r.id.toLowerCase().trim())
                        : templateHeaders.map(h => h.toLowerCase().trim());

                    const missing = requiredHeaders.filter(h => 
                        !headers.some(fh => fh.toLowerCase().trim() === h)
                    );

                    if (missing.length > 0) {
                        // Map back to labels for the error message
                        const missingLabels = missing.map(m => {
                            const req = headerRequirements?.find(r => r.id.toLowerCase().trim() === m);
                            return req ? req.label : m;
                        });
                        setError(`Missing required headers: ${missingLabels.join(', ')}`);
                        return;
                    }

                    setPreviewData(data);
                    setStep('preview');
                },
                error: (err) => setError(`Parsing error: ${err.message}`)
            });
        }
    };

    const processImport = async () => {
        if (previewData.length === 0) return;
        setImporting(true);
        setError(null);

        try {
            await onImport(previewData, zipFile || undefined);
            setResults({ total: previewData.length, success: previewData.length, failed: 0 });
            setStep('success');
        } catch (err: any) {
            setError(err.message || "Failed to process the imported data.");
        } finally {
            setImporting(false);
        }
    };

    const downloadTemplate = () => {
        const csvContent = templateHeaders.join(',') + '\n' + (sampleData || '');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.setAttribute('hidden', '');
        a.setAttribute('href', url);
        a.setAttribute('download', `${title.replace(/\s+/g, '_').toLowerCase()}_template.csv`);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/80 backdrop-blur-md">
            <div className={`bg-white rounded-lg shadow-2xl w-full ${step === 'preview' ? 'max-w-6xl' : 'max-w-2xl'} overflow-hidden transition-all duration-300 transform scale-100`}>
                <div className="bg-gray-100 px-8 py-5 flex justify-between items-center border-b border-gray-200">
                    <div className="flex items-center gap-4">
                        <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center">
                            <i className="fas fa-file-excel text-gray-500 text-xl"></i>
                        </div>
                        <div>
                            <h3 className="text-gray-900 font-bold text-lg tracking-tight leading-none">{title}</h3>
                            <p className="text-gray-500 text-xs font-medium mt-1">
                                {step === 'upload' ? 'Step 1: Upload File' : step === 'preview' ? 'Step 2: Preview & Validate' : 'Complete'}
                            </p>
                        </div>
                    </div>
                    <button onClick={onClose} className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-all">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="p-8">
                    {step === 'upload' && (
                        <div className="space-y-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="space-y-6">
                                    <div className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm">
                                        <h4 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-4">
                                            <i className="fas fa-book-open text-emerald-600"></i> DATA REQUIREMENTS
                                        </h4>
                                        <div className="space-y-4">
                                            {headerRequirements?.map(req => (
                                                <div key={req.id}>
                                                    <div className="flex justify-between items-center mb-1">
                                                        <span className="text-xs font-bold text-gray-700">{req.label}</span>
                                                        {req.required && <span className="text-[10px] font-bold text-red-600 px-2 py-0.5 border border-red-100 rounded bg-red-50">REQUIRED</span>}
                                                    </div>
                                                    <p className="text-[11px] text-gray-500 leading-tight">{req.description}</p>
                                                </div>
                                            ))}
                                            <button
                                                onClick={downloadTemplate}
                                                className="w-full mt-4 flex items-center justify-center gap-2 py-2 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 transition-all shadow-sm"
                                            >
                                                <i className="fas fa-download"></i> Download Template
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col">
                                    <div className="flex-1 group relative border-2 border-dashed border-gray-200 rounded-lg p-8 text-center flex flex-col items-center justify-center hover:border-emerald-500 hover:bg-emerald-50 transition-all cursor-pointer">
                                        <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4 group-hover:bg-emerald-100 transition-all">
                                            <i className="fas fa-cloud-upload-alt text-3xl text-gray-300 group-hover:text-emerald-600"></i>
                                        </div>
                                        <h4 className="text-base font-medium text-gray-900">Upload CSV File</h4>
                                        <p className="text-xs text-gray-400 mt-1">Files limited to 10MB</p>

                                        <input
                                            type="file"
                                            accept=".csv"
                                            onChange={handleFileChange}
                                            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                                        />

                                        {file && (
                                            <div className="mt-4 inline-flex items-center bg-gray-800 px-4 py-2 rounded-md shadow-sm">
                                                <i className="fas fa-file-csv text-emerald-400 mr-2"></i>
                                                <span className="text-xs font-medium text-white">{file.name}</span>
                                            </div>
                                        )}
                                    </div>

                                    {allowFileAttachments && (
                                        <div className="mt-3 border border-dashed border-gray-200 rounded-lg p-4">
                                            <label className="block text-xs font-bold text-gray-700 mb-1">
                                                <i className="fas fa-file-archive text-amber-500 mr-2"></i>
                                                Optional: companion .zip of attachments
                                            </label>
                                            <p className="text-[11px] text-gray-500 mb-3">
                                                Each CSV row may reference a file by its <code className="bg-gray-100 px-1 rounded">pdf_filename</code> column.
                                                Files matched here are uploaded to S3 and attached automatically;
                                                rows without a match are still imported as metadata only.
                                            </p>
                                            <input
                                                type="file"
                                                accept=".zip,application/zip,application/x-zip-compressed"
                                                onChange={(e) => {
                                                    const f = e.target.files?.[0];
                                                    if (!f) return;
                                                    if (f.size > 50 * 1024 * 1024) {
                                                        setError('ZIP too large (max 50 MB).');
                                                        e.target.value = '';
                                                        return;
                                                    }
                                                    setZipFile(f);
                                                    setError(null);
                                                }}
                                                className="text-xs"
                                            />
                                            {zipFile && (
                                                <div className="mt-2 inline-flex items-center bg-amber-50 border border-amber-200 px-3 py-1 rounded">
                                                    <i className="fas fa-file-archive text-amber-600 mr-2"></i>
                                                    <span className="text-xs font-medium text-amber-900">{zipFile.name}</span>
                                                    <button
                                                        type="button"
                                                        onClick={() => setZipFile(null)}
                                                        className="ml-3 text-[10px] font-bold text-rose-500 hover:text-rose-700"
                                                    >
                                                        REMOVE
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {error && (
                                <div className="bg-red-50 border-2 border-red-100 p-4 rounded-2xl flex items-center gap-4 text-red-700 text-sm font-black shadow-sm animate-shake">
                                    <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                                        <i className="fas fa-exclamation-triangle"></i>
                                    </div>
                                    <p>{error}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {step === 'preview' && (
                        <div className="space-y-6">
                            <div className="flex justify-between items-center">
                                <h4 className="text-xl font-black text-gray-900">
                                    Import Preview <span className="text-emerald-500 ml-2">({previewData.length} records found)</span>
                                </h4>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => setStep('upload')}
                                        className="px-4 py-2 text-xs font-black text-gray-500 hover:text-gray-900 transition-colors"
                                    >
                                        <i className="fas fa-undo mr-2"></i> CHANGE FILE
                                    </button>
                                </div>
                            </div>

                            <div className="max-h-[400px] overflow-auto border-2 border-gray-100 rounded-2xl shadow-inner bg-gray-50">
                                <table className="min-w-full divide-y divide-gray-200">
                                    <thead className="bg-gray-100 sticky top-0 z-10">
                                        <tr>
                                            {Object.keys(previewData[0] || {}).map(header => (
                                                <th key={header} className="px-6 py-4 text-left text-[10px] font-black text-gray-500 uppercase tracking-widest border-b border-gray-200">
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {previewData.slice(0, 50).map((row, idx) => (
                                            <tr key={idx} className="hover:bg-emerald-50/50 transition-colors">
                                                {Object.values(row).map((val: any, vidx) => (
                                                    <td key={vidx} className="px-6 py-4 whitespace-nowrap text-xs font-medium text-gray-600">
                                                        {val?.toString() || '-'}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        {previewData.length > 50 && (
                                            <tr>
                                                <td colSpan={100} className="px-6 py-4 text-center text-[10px] font-black text-emerald-600 bg-emerald-50 uppercase tracking-widest">
                                                    + {previewData.length - 50} more records (preview limited to 50)
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {error && <p className="text-red-500 text-sm font-bold">{error}</p>}

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                                <button
                                    onClick={() => setStep('upload')}
                                    className="px-6 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-md transition-all"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={processImport}
                                    disabled={importing}
                                    className="px-8 py-2 bg-emerald-600 text-white text-sm font-medium rounded-md hover:bg-emerald-700 transition-all flex items-center gap-2 disabled:opacity-50"
                                >
                                    {importing ? (
                                        <>
                                            <i className="fas fa-spinner fa-spin"></i> Processing...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-check"></i> Complete Import
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    )}

                    {step === 'success' && results && (
                        <div className="text-center py-10">
                            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fas fa-check text-2xl text-emerald-600"></i>
                            </div>

                            <h3 className="text-2xl font-bold text-gray-900">Import Successful</h3>
                            <p className="text-gray-500 mt-2 font-medium">
                                Successfully processed <b>{results.success}</b> records.
                            </p>

                            <div className="mt-8 grid grid-cols-2 gap-4 max-w-sm mx-auto">
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="text-xl font-bold text-emerald-600">{results.success}</div>
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">SUCCESS</div>
                                </div>
                                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                                    <div className="text-xl font-bold text-red-600">{results.failed}</div>
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">FAILED</div>
                                </div>
                            </div>

                            <button
                                onClick={onClose}
                                className="mt-8 w-full max-w-xs py-2 bg-emerald-600 text-white rounded-md font-medium hover:bg-emerald-700 transition-all shadow-sm"
                            >
                                Done
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
